import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Save, Loader2, AlertTriangle, Upload, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import api, { readError } from "@/lib/api";

const LOCALES = ["hr", "en", "de", "fr"];
const LOCALE_LABELS = { hr: "🇭🇷 Hrvatski", en: "🇬🇧 English", de: "🇩🇪 Deutsch", fr: "🇫🇷 Français" };

const EMPTY = {
  id: "",
  slug_path: "",
  name: { hr: "", en: "", de: "", fr: "" },
  tagline: { hr: "", en: "", de: "", fr: "" },
  description: { hr: "", en: "", de: "", fr: "" },
  hero_image_url: "",
  base_price_eur: 0,
  mode: "custom_build",
  visible: true,
  sort_order: 100,
  customization_fields: [],
};

export default function AdminProductEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === "new";
  const [data, setData] = useState(isNew ? EMPTY : null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isNew) return;
    api.get(`/admin/product-types/${id}`)
      .then((r) => setData({ ...EMPTY, ...r.data, name: { ...EMPTY.name, ...(r.data.name || {}) }, tagline: { ...EMPTY.tagline, ...(r.data.tagline || {}) }, description: { ...EMPTY.description, ...(r.data.description || {}) } }))
      .catch((err) => setError(readError(err, "Could not load product.")));
  }, [id, isNew]);

  const setField = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const setLocField = (k, loc, v) => setData((d) => ({ ...d, [k]: { ...d[k], [loc]: v } }));

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data: r } = await api.post("/admin/uploads", form, { headers: { "Content-Type": "multipart/form-data" } });
      setField("hero_image_url", r.url);
      toast.success(`Image uploaded (${r.width}×${r.height}, ${r.size_kb} KB)`);
    } catch (err) {
      toast.error(readError(err, "Upload failed."));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      const payload = { ...data, base_price_eur: parseFloat(data.base_price_eur) || 0, sort_order: parseInt(data.sort_order, 10) || 100 };
      if (isNew) {
        await api.post("/admin/product-types", payload);
        toast.success("Product created");
        navigate("/admin/products");
      } else {
        await api.put(`/admin/product-types/${id}`, payload);
        toast.success("Product saved");
      }
    } catch (err) {
      setError(readError(err, "Could not save product."));
    } finally {
      setBusy(false);
    }
  };

  if (!data && !error) {
    return <div className="p-10 flex items-center justify-center text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…</div>;
  }

  return (
    <div className="space-y-7 animate-fade-up max-w-4xl">
      <div>
        <Link to="/admin/products" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-3" data-testid="back-to-products">
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} /> All products
        </Link>
        <div className="text-[10px] uppercase tracking-[0.25em] text-primary">{isNew ? "New product" : "Editing"}</div>
        <h1 className="mt-1 font-serif text-4xl text-foreground">{data?.name?.hr || (isNew ? "New product" : id)}</h1>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5" strokeWidth={1.5} /> {error}
        </div>
      )}

      {/* Localized fields */}
      <Card className="bg-card border-border/60 shadow-ambient">
        <CardContent className="p-6 space-y-5">
          <h2 className="font-serif text-lg">Localized content</h2>
          {LOCALES.map((loc) => (
            <div key={loc} className="rounded-lg border border-border/60 p-4 bg-background">
              <div className="text-[10px] uppercase tracking-[0.18em] text-primary mb-3">{LOCALE_LABELS[loc]}</div>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Name</Label>
                  <Input data-testid={`name-${loc}`} value={data.name[loc]} onChange={(e) => setLocField("name", loc, e.target.value)} className="h-10 bg-background" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Tagline</Label>
                  <Input data-testid={`tagline-${loc}`} value={data.tagline[loc]} onChange={(e) => setLocField("tagline", loc, e.target.value)} className="h-10 bg-background" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Description</Label>
                  <Textarea data-testid={`desc-${loc}`} value={data.description[loc]} onChange={(e) => setLocField("description", loc, e.target.value)} rows={4} className="bg-background resize-none" />
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Image */}
      <Card className="bg-card border-border/60 shadow-ambient">
        <CardContent className="p-6 space-y-4">
          <h2 className="font-serif text-lg">Hero image</h2>
          {data.hero_image_url ? (
            <div className="rounded-lg overflow-hidden border border-border/60 bg-background">
              <img src={data.hero_image_url} alt="" className="w-full max-h-64 object-cover" data-testid="product-image-preview" />
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-border p-10 text-center text-sm text-muted-foreground bg-background">
              <ImageIcon className="h-8 w-8 mx-auto mb-2 text-muted-foreground/40" strokeWidth={1.5} />
              No image yet.
            </div>
          )}
          <div className="flex items-center gap-3 flex-wrap">
            <label className="inline-flex items-center gap-2 cursor-pointer rounded-lg border border-border bg-background px-3 py-2 text-sm hover:bg-muted">
              <Upload className="h-3.5 w-3.5" strokeWidth={1.5} />
              {uploading ? "Uploading…" : "Upload image"}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                data-testid="product-image-upload"
                onChange={(e) => onUpload(e.target.files?.[0])}
                disabled={uploading}
              />
            </label>
            <Input
              data-testid="product-image-url"
              value={data.hero_image_url}
              onChange={(e) => setField("hero_image_url", e.target.value)}
              placeholder="or paste an image URL"
              className="h-9 bg-background flex-1 min-w-[200px]"
            />
          </div>
        </CardContent>
      </Card>

      {/* Settings */}
      <Card className="bg-card border-border/60 shadow-ambient">
        <CardContent className="p-6 space-y-4">
          <h2 className="font-serif text-lg">Settings</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Internal ID (slug)</Label>
              <Input value={data.id} onChange={(e) => setField("id", e.target.value)} disabled={!isNew} className="h-10 bg-background font-mono text-sm" data-testid="product-id" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">URL slug path</Label>
              <Input value={data.slug_path} onChange={(e) => setField("slug_path", e.target.value)} className="h-10 bg-background font-mono text-sm" data-testid="product-slug-path" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Base price (EUR)</Label>
              <Input type="number" step="1" min="0" value={data.base_price_eur} onChange={(e) => setField("base_price_eur", e.target.value)} className="h-10 bg-background" data-testid="product-price" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Sort order</Label>
              <Input type="number" step="10" value={data.sort_order} onChange={(e) => setField("sort_order", e.target.value)} className="h-10 bg-background" data-testid="product-sort" />
            </div>
          </div>
          <div className="flex items-center justify-between pt-2">
            <Label htmlFor="visible" className="text-sm">Visible on public site</Label>
            <Switch id="visible" checked={!!data.visible} onCheckedChange={(v) => setField("visible", v)} data-testid="product-visible" />
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center gap-3 pt-2">
        <Button data-testid="save-product-btn" onClick={save} disabled={busy} className="h-11 shadow-interactive">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" strokeWidth={1.5} />}
          {isNew ? "Create product" : "Save changes"}
        </Button>
        <Link to="/admin/products"><Button variant="outline" className="h-11">Cancel</Button></Link>
      </div>
    </div>
  );
}
